import type { IntlShape, MessageDescriptor } from "react-intl";
import {
  ListingApplicationMailKind,
  ListingApplicationMailRecipientSource,
  ListingApplicationMailUnavailableReason,
  MailDeliveryProblem,
  MailDeliveryStatus,
} from "@/graphql/generated/operations";
import {
  LISTING_APPLICATION_MAIL_NOT_ALLOWED,
  LISTING_APPLICATION_MAIL_PREVIEW_GONE,
  LISTING_APPLICATION_MAIL_PREVIEW_STALE,
  LISTING_APPLICATION_MAIL_REQUEST_USED,
  MAIL_NOT_CONFIGURED,
} from "@/lib/genesis-error-codes";
import { graphQlErrorExtensions } from "@/lib/graphql-error";
import { userErrorMessage } from "@/lib/user-error";

// Interview, Angebot und Absage an die bewerbende Person
// (@external-adr GENESIS-0080-an-application-mail-sends-exactly-the-confirmed-preview-to-a-proven-address).
//
// Welche Art geht, sagt `actions.mailKinds`; an wen und in welcher Sprache, entscheidet genesis in der Vorschau. Diese
// Datei benennt nur, was genesis meldet, und prüft einen bearbeiteten Text mit denselben Grenzen vorab, damit HR die
// Ablehnung nicht erst vom Server erfährt. Der Server prüft dieselben Regeln noch einmal.

export const MAIL_KIND_LABELS: Record<ListingApplicationMailKind, MessageDescriptor> = {
  [ListingApplicationMailKind.Interview]: { id: "share.listings.mail.kind.interview", defaultMessage: "Einladung zum Gespräch" },
  [ListingApplicationMailKind.Offer]: { id: "share.listings.mail.kind.offer", defaultMessage: "Angebot" },
  [ListingApplicationMailKind.Rejection]: { id: "share.listings.mail.kind.rejection", defaultMessage: "Absage" },
  // Sendet genesis selbst nach einer öffentlich bestätigten Bewerbung; HR kann sie weder vorschauen noch senden
  // (@external-adr GENESIS-0082-a-confirmed-application-owes-one-receipt-written-outside-the-confirmation).
  [ListingApplicationMailKind.Receipt]: { id: "share.listings.mail.kind.receipt", defaultMessage: "Eingangsbestätigung" },
};

/** Was SMTP sagt, nicht mehr: angenommen heißt nicht zugestellt (GENESIS-0081). */
export const MAIL_STATUS_LABELS: Record<MailDeliveryStatus, MessageDescriptor> = {
  [MailDeliveryStatus.Queued]: { id: "share.listings.mail.status.queued", defaultMessage: "Wartet auf Versand" },
  [MailDeliveryStatus.Sending]: { id: "share.listings.mail.status.sending", defaultMessage: "Wird gesendet" },
  [MailDeliveryStatus.SmtpAccepted]: { id: "share.listings.mail.status.smtpAccepted", defaultMessage: "Vom Mailserver angenommen" },
  [MailDeliveryStatus.FailedBeforeSend]: { id: "share.listings.mail.status.failedBeforeSend", defaultMessage: "Nicht gesendet" },
  [MailDeliveryStatus.DeliveryUnknown]: { id: "share.listings.mail.status.deliveryUnknown", defaultMessage: "Versand unklar" },
  [MailDeliveryStatus.Cancelled]: { id: "share.listings.mail.status.cancelled", defaultMessage: "Abgebrochen" },
};

export const MAIL_PROBLEM_LABELS: Record<MailDeliveryProblem, MessageDescriptor> = {
  [MailDeliveryProblem.NotConfigured]: { id: "share.listings.mail.problem.notConfigured" },
  [MailDeliveryProblem.SmtpUnavailable]: { id: "share.listings.mail.problem.smtpUnavailable" },
  [MailDeliveryProblem.RequesterWithoutAccess]: { id: "share.listings.mail.problem.requesterWithoutAccess" },
  [MailDeliveryProblem.ApplicationState]: { id: "share.listings.mail.problem.applicationState" },
  [MailDeliveryProblem.RecipientChanged]: { id: "share.listings.mail.problem.recipientChanged" },
  [MailDeliveryProblem.OutcomeUnknown]: { id: "share.listings.mail.problem.outcomeUnknown" },
  [MailDeliveryProblem.Interrupted]: { id: "share.listings.mail.problem.interrupted" },
  [MailDeliveryProblem.CheckFailed]: { id: "share.listings.mail.problem.checkFailed" },
  // Nur bei Benachrichtigungen an Lesende (GENESIS-0085), nie in der Historie einer Bewerbung; der Vollständigkeit halber.
  [MailDeliveryProblem.RecipientOptedOut]: { id: "share.listings.mail.problem.recipientOptedOut" },
  [MailDeliveryProblem.RecipientWithoutAccess]: { id: "share.listings.mail.problem.recipientWithoutAccess" },
};

export const RECIPIENT_SOURCE_LABELS: Record<ListingApplicationMailRecipientSource, MessageDescriptor> = {
  [ListingApplicationMailRecipientSource.FormConfirmation]: { id: "share.listings.mail.source.formConfirmation" },
  [ListingApplicationMailRecipientSource.VerifiedLogin]: { id: "share.listings.mail.source.verifiedLogin" },
};

export const UNAVAILABLE_REASON_LABELS: Record<ListingApplicationMailUnavailableReason, MessageDescriptor> = {
  [ListingApplicationMailUnavailableReason.NoProvenAddress]: { id: "share.listings.mail.unavailable.noProvenAddress" },
  [ListingApplicationMailUnavailableReason.LoginNotVerified]: { id: "share.listings.mail.unavailable.loginNotVerified" },
  [ListingApplicationMailUnavailableReason.LoginUnavailable]: { id: "share.listings.mail.unavailable.loginUnavailable" },
  [ListingApplicationMailUnavailableReason.PersonInactive]: { id: "share.listings.mail.unavailable.personInactive" },
};

/** Die Grenzen der Vorlagen in genesis (ListingApplicationMails), damit ein bearbeiteter Text nicht länger wird. */
export const MAX_SUBJECT_LENGTH = 200;
export const MAX_BODY_LENGTH = 5000;
export const MAX_APPOINTMENT_LENGTH = 200;
export const MAX_MEETING_LINK_LENGTH = 500;

const LEFTOVER_PLACEHOLDER = /\{(?:appName|listingName|reference|appointment|link)\}/;
// eslint-disable-next-line no-control-regex
const NOT_IN_A_LINE = /[\u0000-\u001f\u007f-\u009f\u2028\u2029\u202a-\u202e\u2066-\u2069]/;
// eslint-disable-next-line no-control-regex
const NOT_IN_A_TEXT = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u2028\u2029\u202a-\u202e\u2066-\u2069]/;

export type MailTextProblem = "subject" | "body" | "placeholder";

/** Was genesis an einem bearbeiteten Text ablehnen würde, oder null. Zeilenumbrüche im Text sind erlaubt. */
export function mailTextProblem(subject: string, body: string): MailTextProblem | null {
  const line = subject.trim();
  const text = body.replace(/\r\n?/g, "\n").trim();
  if (!line || line.length > MAX_SUBJECT_LENGTH || NOT_IN_A_LINE.test(line)) return "subject";
  if (!text || text.length > MAX_BODY_LENGTH || NOT_IN_A_TEXT.test(text)) return "body";
  if (LEFTOVER_PLACEHOLDER.test(line) || LEFTOVER_PLACEHOLDER.test(text)) return "placeholder";
  return null;
}

/** Ob ein Terminlink so aussieht, wie genesis ihn annimmt: https, ein Host, keine Zugangsdaten. Leer ist erlaubt. */
export function meetingLinkValid(link: string): boolean {
  const value = link.trim();
  if (!value) return true;
  if (value.length > MAX_MEETING_LINK_LENGTH || /\s/.test(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !!url.hostname && !url.username && !url.password;
  } catch {
    return false;
  }
}

/** Die Ablehnungen, die HR im Dialog selbst auflöst, in eigenen Worten; alles andere generisch (SPARK-0010). */
export function mailErrorMessage(intl: IntlShape, error: unknown): string {
  if (graphQlErrorExtensions(error, LISTING_APPLICATION_MAIL_PREVIEW_STALE)) {
    return intl.formatMessage({ id: "share.listings.mail.error.stale" });
  }
  if (graphQlErrorExtensions(error, LISTING_APPLICATION_MAIL_PREVIEW_GONE)) {
    return intl.formatMessage({ id: "share.listings.mail.error.gone" });
  }
  if (graphQlErrorExtensions(error, LISTING_APPLICATION_MAIL_REQUEST_USED)) {
    return intl.formatMessage({ id: "share.listings.mail.error.requestUsed" });
  }
  if (graphQlErrorExtensions(error, MAIL_NOT_CONFIGURED)) {
    return intl.formatMessage({ id: "share.listings.mail.error.notConfigured" });
  }
  if (graphQlErrorExtensions(error, LISTING_APPLICATION_MAIL_NOT_ALLOWED)) {
    return intl.formatMessage({ id: "share.listings.mail.error.notAllowed" });
  }
  return userErrorMessage(intl, error);
}

/** Ob die Antwort die Vorschau ungültig macht: dann gibt es nichts mehr zu bestätigen, nur neu vorzuschauen. */
export function invalidatesPreview(error: unknown): boolean {
  return [LISTING_APPLICATION_MAIL_PREVIEW_STALE, LISTING_APPLICATION_MAIL_PREVIEW_GONE,
    LISTING_APPLICATION_MAIL_REQUEST_USED, LISTING_APPLICATION_MAIL_NOT_ALLOWED]
    .some((code) => graphQlErrorExtensions(error, code) !== null);
}
